import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Recycle, TreePine, Droplets, Wind, Earth, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/shared/SEO';
import { getSustainabilityContent } from '@/lib/api';

// Default eco products (fallback)
const defaultEcoProducts = [
  {
    title: 'Eco Guest Amenities',
    description: 'Organic soaps, bamboo toothbrushes, jute slippers, compostable shower caps — premium eco-friendly guest essentials',
    image_url: '/assets/images/products/eco-amenities.webp',
  },
  {
    title: 'Eco Food Packaging',
    description: 'Kraft paper bags, compostable carry bags, biodegradable deli wraps & cups — sustainable food service packaging',
    image_url: '/assets/images/products/eco-food-packaging.webp',
  },
  {
    title: 'Eco Disposables',
    description: 'Wooden cutlery, paper straws, bamboo skewers, palm leaf boats — 100% eco-friendly and compostable disposables',
    image_url: '/assets/images/products/eco-disposables.webp',
  },
  {
    title: 'Bagasse Disposables',
    description: 'Sugarcane bagasse plates, bowls, containers & trays — biodegradable tableware made from plant-based materials',
    image_url: '/assets/images/products/bagasse-disposables.webp',
  },
];

const defaultSettings = {
  hero_heading: 'Eco-Friendly Solutions for a Greener Future',
  hero_subheading: "We're committed to providing sustainable hospitality supplies that help reduce environmental impact without compromising on quality or performance.",
  hero_image_url: '/assets/images/products/sustainable-eco-packaging.webp',
  commitment_heading: 'Our Sustainability Commitment',
  commitment_description: "At Shubha Lakshmi, sustainability isn't just a buzzword—it's a core value that guides our business decisions. We're committed to helping the hospitality industry transition to more sustainable practices.",
  commitment_image_url: '/assets/images/products/sustainable-packaging.webp',
  commitment_badge: 'Going Green Since 2016',
  commitments: [
    'Partner with certified eco-friendly manufacturers',
    'Continuous expansion of sustainable product range',
    'Educate clients on environmental benefits',
    'Minimize packaging waste in deliveries',
    'Support local and regional suppliers',
    'Regular sustainability audits and improvements',
  ],
  stats: [
    { label: 'Eco Products', value: '50+' },
    { label: 'Biodegradable', value: '100%' },
    { label: 'Eco Clients', value: '200+' },
    { label: 'Years Experience', value: '8+' },
  ],
};

export default function SustainabilityPage() {
  const [pageSettings, setPageSettings] = useState({ ...defaultSettings });
  const [ecoProducts, setEcoProducts] = useState(defaultEcoProducts);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await getSustainabilityContent();
        const { settings, showcases } = res.data;
        if (settings && Object.keys(settings).length > 0) {
          setPageSettings(prev => ({ ...prev, ...settings }));
        }
        if (showcases && showcases.length > 0) {
          setEcoProducts(showcases);
        }
      } catch (err) {
        // Keep defaults on error
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const benefits = [
    { icon: Leaf, title: 'Biodegradable', desc: 'Products break down naturally within months' },
    { icon: Recycle, title: 'Recyclable', desc: 'Materials can be recycled and reused' },
    { icon: TreePine, title: 'Renewable Sources', desc: 'Made from sustainable plant-based materials' },
    { icon: Droplets, title: 'Water Efficient', desc: 'Minimal water usage in manufacturing' },
    { icon: Wind, title: 'Low Carbon', desc: 'Reduced carbon footprint in production' },
    { icon: Earth, title: 'Earth Friendly', desc: 'No harmful chemicals or toxins' },
  ];

  // Split heading for green highlight (split on last two words or use full heading)
  const headingParts = pageSettings.hero_heading.split(' ');
  const greenWords = headingParts.length > 2 ? headingParts.slice(-2).join(' ') : '';
  const mainWords = headingParts.length > 2 ? headingParts.slice(0, -2).join(' ') : pageSettings.hero_heading;

  return (
    <div className="min-h-screen" data-testid="sustainability-page">
      <SEO title="Sustainability" description="Our commitment to eco-friendly hospitality - biodegradable products, sustainable packaging, and green alternatives for the hospitality industry." />
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden" data-testid="sustainability-hero">
        <div className="absolute inset-0 bg-[#F0FDF4]" />
        <div className="absolute inset-0 opacity-5">
          <img
            src="https://images.unsplash.com/photo-1708352411884-d8e06811ed27?w=1920"
            alt="Texture"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Leaf className="w-6 h-6 text-green-600" />
                <span className="section-overline text-green-600">Sustainability</span>
              </div>
              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary leading-tight"
                style={{ fontFamily: 'Manrope' }}
              >
                {mainWords}{' '}
                {greenWords && <span className="text-green-600">{greenWords}</span>}
              </h1>
              <p className="text-lg text-muted-foreground mt-6 leading-relaxed">
                {pageSettings.hero_subheading}
              </p>
              <div className="flex gap-4 mt-8">
                <Link to="/products?eco=true">
                  <Button className="rounded-full bg-green-600 hover:bg-green-700 text-white px-8" data-testid="eco-products-btn">
                    View Eco Products
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="rounded-full border-green-600 text-green-600 hover:bg-green-50" data-testid="eco-quote-btn">
                    Get Quote
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              {pageSettings.hero_image_url && (
                <img
                  src={pageSettings.hero_image_url}
                  alt="Eco packaging"
                  className="rounded-2xl shadow-2xl"
                />
              )}
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl max-w-xs">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Leaf className="w-5 h-5 text-green-600" />
                  </div>
                  <p className="font-bold text-primary" style={{ fontFamily: 'Manrope' }}>Eco-Friendly Materials</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  Our products are made from sustainable, recyclable, and biodegradable materials
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 md:py-32 bg-white" data-testid="eco-benefits">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="section-overline text-green-600">Why Go Eco</span>
            <h2 className="section-title text-4xl md:text-5xl mt-4" style={{ fontFamily: 'Manrope' }}>
              Benefits of Eco-Friendly Products
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className="bg-[#FAFAF9] p-8 rounded-2xl card-lift"
              >
                <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                  <benefit.icon className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="font-semibold text-primary text-lg mb-2" style={{ fontFamily: 'Manrope' }}>
                  {benefit.title}
                </h3>
                <p className="text-sm text-muted-foreground">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eco Products Showcase */}
      <section className="py-24 md:py-32 bg-[#F0FDF4]" data-testid="eco-products-showcase">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="section-overline text-green-600">Our Range</span>
            <h2 className="section-title text-4xl md:text-5xl mt-4" style={{ fontFamily: 'Manrope' }}>
              Eco-Friendly Product Range
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              Discover our comprehensive range of sustainable products designed for the hospitality industry
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {ecoProducts.map((product, idx) => (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-2xl bg-white shadow-lg card-lift"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={product.image_url}
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="eco-badge">Eco-Friendly</span>
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-2" style={{ fontFamily: 'Manrope' }}>
                    {product.title}
                  </h3>
                  <p className="text-muted-foreground">{product.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/products?eco=true">
              <Button className="rounded-full bg-green-600 hover:bg-green-700 text-white px-8" data-testid="view-all-eco-btn">
                View All Eco Products
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-24 md:py-32 bg-white" data-testid="eco-commitment">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="section-overline text-green-600">Our Promise</span>
              <h2 className="section-title text-4xl md:text-5xl mt-4" style={{ fontFamily: 'Manrope' }}>
                {pageSettings.commitment_heading}
              </h2>
              <p className="text-muted-foreground mt-6 leading-relaxed">
                {pageSettings.commitment_description}
              </p>

              <ul className="mt-8 space-y-4">
                {pageSettings.commitments.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              {pageSettings.commitment_image_url && (
                <img
                  src={pageSettings.commitment_image_url}
                  alt="Sustainable packaging"
                  className="rounded-2xl"
                />
              )}
              {pageSettings.commitment_badge && (
                <div className="absolute top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  {pageSettings.commitment_badge}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-green-600" data-testid="eco-stats">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {pageSettings.stats.map((stat, idx) => (
              <div key={idx}>
                <p className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{stat.value}</p>
                <p className="text-white/70 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#FAFAF9]" data-testid="eco-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-primary mb-6" style={{ fontFamily: 'Manrope' }}>
            Make the Switch to Sustainable
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            Join hundreds of businesses that have switched to eco-friendly hospitality supplies.
            Contact us to learn how we can help your business go green.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="rounded-full bg-green-600 hover:bg-green-700 text-white px-8 py-6 font-semibold"
                data-testid="eco-contact-btn"
              >
                Start Your Green Journey
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/products">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-green-600 text-green-600 hover:bg-green-50 px-8 py-6 font-semibold"
                data-testid="eco-explore-btn"
              >
                Explore Products
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
